import React, { useState } from "react";
import CalibrationApi from "../../../../api/CalibrationApi";
import { EModuleType } from "../../../../Model/EModuleType";
import { EArmType, CartesianWaypoint } from "../../../../Model/Waypoint";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import CartesianArmMover from "../../../Common/CartesianArmMover";
import { ECarrier } from "../../../../Model/ECarrier";
import CommonDropDown from "../../../Common/DropDown/CommonDropDown";
import MtiButton from "../../../Buttons/MtiButton";
import AutoCalibrationUtil from "../../../../Utility/AutoCalibrationUtil";
import "./SlotCorrection.css";
import "../../VectorEditor/SettingTools.css";
import "../HotelCalibration/HotelOffsetView.css";
import "../StorageCalibration/AutoCalibration/AutoCalibrationResultView.css";
import VectorEditorComponent from "../../VectorEditor/VectorEditorComponent";
import SwitchButton from "../../../Buttons/SwitchButton";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";

type Prop = {
  carrier: ECarrier;
};

type CarrierDisplayValue = {
  display: string;
  value: ECarrier;
  disabled: boolean;
};

const carriersSelection: CarrierDisplayValue[] = [
  ECarrier.Imager,
  ECarrier.LoadPort,
  ECarrier.FixedCarriage,
  ECarrier.MovingCarriage1,
  ECarrier.MovingCarriage2,
].map((carrier) => ({
  display: AutoCalibrationUtil.getCarrierName(carrier),
  disabled: false,
  value: carrier,
}));

export default function ArmMover() {
  const psrModule = useTypedSelector(
    (state) => getModules(state, EModuleType.PSR)[0]
  );

  const [selectedCarrier, setSelectedCarrier] = useState(
    ECarrier.FixedCarriage
  );

  const moduleState = useTypedSelector((state) =>
    getModuleState(state, psrModule.identifier)
  );

  if (moduleState == null) {
    throw new Error("Suite state is null");
  }

  const psrState = moduleState.psr;
  const isPickUpMotorExtended = psrState?.isPickUpMotorExtended;
  const isElectromagnetEngaged = psrState?.isElectromagnetEngaged;

  const setElectromagnet = (isActivated: boolean) => {
    CalibrationApi.setElectromagnet(psrModule.identifier, isActivated);
  };

  const handleExtend = () => {
    CalibrationApi.setPickupMotor(psrModule.identifier, true);
  };

  const handleRetract = () => {
    CalibrationApi.setPickupMotor(psrModule.identifier, false);
  };

  return (
    <>
      <div
        style={{
          padding: "15px 19px",
          backgroundColor: "#414042",
          display: "flex",
          alignItems: "center",
          fontFamily: "Roboto",
          fontSize: "20px",
          fontWeight: 500,
          textAlign: "left",
          color: "white",
        }}
      >
        Arm Mover
      </div>
      <div className="control-panel">
        <div className="small-container">
          <div style={{ width: "100%" }}>
            <CartesianArmMover
              armType={EArmType.PsrArm}
              moduleIdentifier={psrModule.identifier}
            />
          </div>
        </div>
        <div
          className="small-container"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CommonDropDown<number>
            value={selectedCarrier}
            id="carrier-select"
            onChange={(value) => {
              setSelectedCarrier(value as ECarrier);
            }}
            style={{
              width: 389,
              minHeight: 32,
              borderRadius: 4,
            }}
            dropDownDisplayValues={carriersSelection}
          />
          <MtiButton
            onClick={() =>
              CalibrationApi.ExpandCarrier(
                psrModule.identifier,
                selectedCarrier
              )
            }
            style={{
              alignSelf: "flex-end",
              letterSpacing: 1.25,
              minHeight: 32,
              width: 106,
              fontSize: 16,
              marginLeft: 16,
              marginTop: 7,
              marginBottom: 7,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 6L6 7.5L2.25 3.75L0.75 5.25V0.75H5.25L3.75 2.25L7.5 6Z"
                fill="white"
              />
              <path
                d="M10.5 6L12 7.5L15.75 3.75L17.25 5.25V0.75H12.75L14.25 2.25L10.5 6Z"
                fill="white"
              />
              <path
                d="M6 10.5L7.5 12L3.75 15.75L5.25 17.25H0.75V12.75L2.25 14.25L6 10.5Z"
                fill="white"
              />
              <path
                d="M10.5 12L12 10.5L15.75 14.25L17.25 12.75V17.25H12.75L14.25 15.75L10.5 12Z"
                fill="white"
              />
            </svg>
            <span>OPEN</span>
          </MtiButton>
        </div>
        <div className="small-container">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {isPickUpMotorExtended != null && (
              <div style={{ display: "flex", gap: "14px" }}>
                <MtiButton
                  colorVariant="alternate"
                  variant="secondary"
                  size="medium"
                  disabled={isPickUpMotorExtended}
                  onClick={handleExtend}
                  style={{
                    minWidth: 40,
                    height: 32,
                  }}
                >
                  Extend
                </MtiButton>
                <MtiButton
                  colorVariant="alternate"
                  variant="secondary"
                  size="medium"
                  disabled={!isPickUpMotorExtended}
                  onClick={handleRetract}
                  style={{ minWidth: 40, height: 32 }}
                >
                  Retract
                </MtiButton>
              </div>
            )}
            <div>
              <div
                style={{ display: "flex", gap: "14px", alignItems: "center" }}
                className="setting-tools-action-button"
              >
                <span>Electromagnet</span>
                {isElectromagnetEngaged !== null &&
                  isElectromagnetEngaged !== undefined && (
                    <SwitchButton
                      withLetter={true}
                      isOn={isElectromagnetEngaged}
                      onToggle={() => setElectromagnet(!isElectromagnetEngaged)}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
